import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Where the starting-soon card, and the higher-priority start banners
 * ("Your circle is starting!", "{group} is live!", #1157), sit (David,
 * 2026-10-30, placement A): just
 * below the page's header, at every width, on both hosts (the app shell and
 * the public site). A header that scrolls away (the app's phone header is in
 * the flow) takes the card with it only as far as the top of the viewport: it
 * then rests under the safe-area inset, never mid-page over content.
 *
 * The header marks itself with `data-notice-anchor`; this measures its bottom
 * edge and hands it to the CSS as `--ee-notice-anchor-bottom` on the card's
 * host, where `top: max(safe-area + gap, anchor bottom + gap)` does the clamp
 * (env() is not readable from script). No anchor on the page: the CSS
 * fallback keeps the card at the top under the safe area, as before.
 *
 * It also publishes the card's own bottom edge as `--ee-top-notice-bottom` on
 * <html>, so the top toasts (design-system/toasts.css) stack under the card
 * instead of over it, and removes it when the card goes. ONE surface holds
 * that offset at a time: the newest one up. When the banner takes the slot
 * from the card, the card's exit finishes after the banner mounted, and its
 * cleanup must not wipe the banner's offset; when the holder goes, the one
 * still up (if any) takes it back.
 *
 * Written straight to the element's style, not through React state: a scroll
 * re-renders nothing.
 */
export const NOTICE_ANCHOR_ATTR = 'data-notice-anchor';
export const ANCHOR_BOTTOM_VAR = '--ee-notice-anchor-bottom';
export const NOTICE_BOTTOM_VAR = '--ee-top-notice-bottom';

/** The first header on the page that is laid out (a `hidden` one measures 0). */
export function findNoticeAnchor(doc: Document = document): HTMLElement | null {
  for (const el of Array.from(doc.querySelectorAll<HTMLElement>(`[${NOTICE_ANCHOR_ATTR}]`))) {
    const r = el.getBoundingClientRect();
    if (r.height > 0 && r.width > 0) return el;
  }
  return null;
}

/** The surfaces up now, oldest first; the last one holds the toast offset. */
const holders: HTMLElement[] = [];

function publishBottom(host: HTMLElement, doc: Document): void {
  // offsetTop ignores the enter animation's transform, so this is where the
  // card comes to rest, not where it is mid-slide.
  doc.documentElement.style.setProperty(
    NOTICE_BOTTOM_VAR,
    `${Math.round(host.offsetTop + host.offsetHeight)}px`,
  );
}

function holdsOffset(host: HTMLElement): boolean {
  return holders.length === 0 || holders[holders.length - 1] === host;
}

/** Measure once: the anchor's bottom onto the host, the host's bottom onto <html>. Returns the anchor. */
export function placeBelowHeader(host: HTMLElement, doc: Document = document): HTMLElement | null {
  const anchor = findNoticeAnchor(doc);
  if (anchor) {
    host.style.setProperty(
      ANCHOR_BOTTOM_VAR,
      `${Math.round(anchor.getBoundingClientRect().bottom)}px`,
    );
  } else {
    host.style.removeProperty(ANCHOR_BOTTOM_VAR);
  }
  if (holdsOffset(host)) publishBottom(host, doc);
  return anchor;
}

export function useBelowHeader(host: HTMLElement | null, pathname: string): void {
  useEffect(() => {
    if (!host) return;
    const doc = host.ownerDocument;
    let frame = 0;
    let watched: HTMLElement | null = null;
    const sizes =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => schedule());
    const place = () => {
      const anchor = placeBelowHeader(host, doc);
      // The header's own size (the public bar's menu opens inside it; it is
      // fixed, so the body does not resize with it).
      if (anchor !== watched) {
        if (watched) sizes?.unobserve(watched);
        if (anchor) sizes?.observe(anchor);
        watched = anchor;
      }
    };
    function schedule() {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        place();
      });
    }

    holders.push(host);
    place();
    // Any scroller (the window, or a page's own scrolling box), the viewport,
    // and anything that changes the page's or the card's size (a lazy page
    // mounting its header, the card's error line wrapping).
    doc.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule);
    sizes?.observe(doc.body);
    sizes?.observe(host);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      doc.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
      sizes?.disconnect();
      const at = holders.lastIndexOf(host);
      if (at !== -1) holders.splice(at, 1);
      const next = holders[holders.length - 1];
      if (next) publishBottom(next, next.ownerDocument);
      else doc.documentElement.style.removeProperty(NOTICE_BOTTOM_VAR);
    };
    // pathname: a route change can swap the header without resizing the body.
  }, [host, pathname]);
}

/**
 * The host for a surface whose element can be replaced while the old one is
 * still mounted: inside an AnimatePresence a keyed element plays its exit
 * while its successor mounts, and the old one's detach (null) lands AFTER the
 * new one's attach. A shared `ref={setHost}` then handed the new element null
 * and it stopped following the header (#1157 review, f79c57f9b). Each key gets
 * its own ref callback, which clears the host only while the host is still
 * its element. Used by the starting-soon card (keyed by circle) and the start
 * banners.
 */
export function useBelowHeaderHost(
  pathname: string,
): (key: number | string) => (el: HTMLDivElement | null) => void {
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  useBelowHeader(host, pathname);
  const refs = useRef(new Map<number | string, (el: HTMLDivElement | null) => void>());
  return useCallback((key: number | string) => {
    let attach = refs.current.get(key);
    if (!attach) {
      let mine: HTMLDivElement | null = null;
      attach = (el) => {
        if (el) {
          mine = el;
          setHost(el);
          return;
        }
        const gone = mine;
        mine = null;
        setHost((h) => (h === gone ? null : h));
      };
      refs.current.set(key, attach);
    }
    return attach;
  }, []);
}
